import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="https://public.readdy.ai/ai/img_res/01637527-6d22-443a-bf67-fb4581f8a33b.png" alt="BANARICA LTDA Logo" className="h-12 w-12 object-contain" />
            <h1 className="text-2xl font-bold text-gray-900">BANARICA LTDA</h1>
          </div>
          <div className="flex items-center gap-12">
            <button onClick={() => scrollToSection('products')} className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Products</button>
            <button onClick={() => scrollToSection('features')} className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Features</button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-[#0A1628] via-[#1E3A5F] to-[#2A4A7C]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-20 w-64 h-64 border-2 border-white rounded-3xl rotate-12"></div>
          <div className="absolute top-40 right-32 w-48 h-48 border-2 border-white rounded-2xl -rotate-6"></div>
          <div className="absolute bottom-32 left-1/3 w-56 h-56 border-2 border-white rounded-3xl rotate-45"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-8 py-32 w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div className="inline-block px-6 py-2 bg-[#00E5CC]/20 border border-[#00E5CC] rounded-full">
              <span className="text-[#00E5CC] text-sm font-medium whitespace-nowrap">iOS Development Experts</span>
            </div>
            
            <div className="space-y-4">
              <h2 className="text-7xl font-bold text-white leading-tight">Modern Applications</h2>
              <h2 className="text-7xl font-bold text-white leading-tight">for iOS Platform</h2>
              <h2 className="text-6xl font-semibold text-[#00E5CC] leading-tight">Security & Performance</h2>
            </div>
            
            <div className="space-y-6 max-w-xl">
              <p className="text-lg text-gray-300 leading-relaxed">
                We are a team of experts developing modern applications for the iOS platform. Our products are designed to enhance user experience and improve the security of mobile devices.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                With cutting-edge technology and innovative solutions, we deliver applications that meet the highest standards of quality and performance.
              </p>
            </div>
          </div>
          
          <div className="relative flex items-center justify-center">
            <div className="relative transform rotate-6 hover:rotate-0 transition-transform duration-500">
              <img 
                src="https://public.readdy.ai/ai/img_res/4d24933fca27d919451646f50d6d070f.jpg" 
                alt="iOS Application Interface" 
                className="w-full max-w-md rounded-3xl shadow-2xl"
                style={{ filter: 'drop-shadow(0 40px 80px rgba(0,0,0,0.4))' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            <div className="lg:col-span-2 space-y-6">
              <div className="text-[#00E5CC] text-xs font-bold tracking-widest uppercase">WHO WE ARE</div>
              <div className="space-y-2">
                <h3 className="text-5xl font-light text-gray-400">Expert Team</h3>
                <h3 className="text-6xl font-bold italic text-gray-900">Building Excellence</h3>
              </div>
            </div>
            
            <div className="lg:col-span-3 space-y-6 lg:pl-16">
              <p className="text-lg text-gray-700 leading-relaxed">
                BANARICA LTDA is a dedicated team of iOS development specialists committed to creating innovative mobile solutions. Our expertise spans across security applications, system optimization tools, and user-centric design.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We focus on delivering applications that not only meet but exceed user expectations. Our products, including VPN and Cleaner applications, are built with the latest iOS technologies to ensure optimal performance, security, and user satisfaction.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With years of experience in mobile development, we understand the importance of privacy, speed, and reliability. Every line of code we write is crafted with precision and care to provide users with the best possible experience on their iOS devices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-end justify-between mb-16">
            <div className="space-y-3">
              <h3 className="text-5xl font-bold text-gray-900">
                Our Products <span className="text-red-500">Portfolio</span>
              </h3>
              <p className="text-gray-600 text-lg">VPN & Cleaner Solutions</p>
            </div>
            <div className="px-6 py-2 bg-gray-900 text-white rounded-full text-sm font-medium whitespace-nowrap">
              2 Products
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* VPN Product Card */}
            <div className="group bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-12 hover:-translate-y-2 transition-all duration-300 shadow-lg hover:shadow-2xl cursor-pointer">
              <div className="flex flex-col items-center text-center space-y-8">
                <div className="w-32 h-32 flex items-center justify-center">
                  <i className="ri-shield-check-line text-8xl text-blue-600"></i>
                </div>
                <div className="space-y-4">
                  <h4 className="text-4xl font-bold text-gray-900">VPN</h4>
                  <p className="text-xl font-semibold text-gray-700">Security Application</p>
                  <p className="text-gray-600 leading-relaxed max-w-md">
                    Protect your online privacy with military-grade encryption. Browse securely and anonymously with our advanced VPN solution designed specifically for iOS devices.
                  </p>
                </div>
                <div className="pt-6">
                  <span className="text-2xl font-bold text-gray-900">Premium</span>
                </div>
              </div>
            </div>

            {/* Cleaner Product Card */}
            <div className="group bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-12 hover:-translate-y-2 transition-all duration-300 shadow-lg hover:shadow-2xl cursor-pointer">
              <div className="flex flex-col items-center text-center space-y-8">
                <div className="w-32 h-32 flex items-center justify-center">
                  <i className="ri-brush-line text-8xl text-green-600"></i>
                </div>
                <div className="space-y-4">
                  <h4 className="text-4xl font-bold text-gray-900">Cleaner</h4>
                  <p className="text-xl font-semibold text-gray-700">Optimization Tool</p>
                  <p className="text-gray-600 leading-relaxed max-w-md">
                    Keep your iOS device running smoothly with our intelligent cleaning tool. Remove junk files, optimize storage, and boost performance with just one tap.
                  </p>
                </div>
                <div className="pt-6">
                  <span className="text-2xl font-bold text-gray-900">Premium</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
            <div className="space-y-3">
              <h3 className="text-6xl font-light text-gray-400">Advanced</h3>
              <h3 className="text-7xl font-bold italic text-gray-900">Features</h3>
            </div>
            <div className="flex items-center">
              <p className="text-lg text-gray-600 leading-relaxed">
                Our applications are packed with powerful features designed to provide the best user experience, security, and performance on iOS devices.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature Card 1 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 border border-gray-100">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=abstract%20digital%20security%20encryption%20concept%20with%20glowing%20blue%20shield%20and%20lock%20symbols%2C%20modern%20technology%20illustration%2C%20clean%20minimalist%20style%20with%20gradient%20background%2C%20cybersecurity%20theme&width=400&height=300&seq=feature-security-001&orientation=landscape" 
                  alt="Security Feature" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8 space-y-4">
                <div className="inline-block px-4 py-1 bg-[#B8FF3C] text-gray-900 text-xs font-bold rounded-full whitespace-nowrap">SECURITY</div>
                <h4 className="text-2xl font-bold text-gray-900">Military-Grade Encryption</h4>
                <p className="text-gray-600 leading-relaxed">
                  Your data is protected with AES-256 encryption, the same standard used by governments and security experts worldwide.
                </p>
              </div>
            </div>

            {/* Feature Card 2 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 border border-gray-100">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=fast%20speed%20performance%20concept%20with%20dynamic%20motion%20blur%20and%20velocity%20lines%2C%20modern%20abstract%20technology%20visualization%2C%20sleek%20gradient%20design%2C%20high%20performance%20theme&width=400&height=300&seq=feature-speed-002&orientation=landscape" 
                  alt="Performance Feature" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8 space-y-4">
                <div className="inline-block px-4 py-1 bg-[#B8FF3C] text-gray-900 text-xs font-bold rounded-full whitespace-nowrap">PERFORMANCE</div>
                <h4 className="text-2xl font-bold text-gray-900">Lightning Fast Speed</h4>
                <p className="text-gray-600 leading-relaxed">
                  Optimized algorithms ensure minimal impact on device performance while delivering maximum efficiency and speed.
                </p>
              </div>
            </div>

            {/* Feature Card 3 */}
            <div className="bg-gradient-to-br from-[#0F1C2E] to-[#1a2942] rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow duration-300">
              <div className="flex flex-col items-center text-center space-y-6 h-full justify-center">
                <div className="w-24 h-24 flex items-center justify-center">
                  <i className="ri-shield-star-line text-7xl text-[#00E5CC]"></i>
                </div>
                <div className="inline-block px-4 py-1 bg-[#B8FF3C] text-gray-900 text-xs font-bold rounded-full whitespace-nowrap">PRIVACY</div>
                <h4 className="text-2xl font-bold text-white">No-Log Policy</h4>
                <p className="text-gray-300 leading-relaxed">
                  We never track, collect, or store your personal data. Your privacy is our top priority.
                </p>
              </div>
            </div>

            {/* Feature Card 4 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 border border-gray-100">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=user%20friendly%20interface%20design%20with%20clean%20modern%20iOS%20app%20screens%2C%20intuitive%20navigation%20elements%2C%20minimalist%20UI%20components%2C%20professional%20app%20design%20showcase&width=400&height=300&seq=feature-interface-003&orientation=landscape" 
                  alt="Interface Feature" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8 space-y-4">
                <div className="inline-block px-4 py-1 bg-[#B8FF3C] text-gray-900 text-xs font-bold rounded-full whitespace-nowrap">DESIGN</div>
                <h4 className="text-2xl font-bold text-gray-900">Intuitive Interface</h4>
                <p className="text-gray-600 leading-relaxed">
                  Beautiful, user-friendly design that makes complex features simple and accessible for everyone.
                </p>
              </div>
            </div>

            {/* Feature Card 5 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 border border-gray-100">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=smart%20automation%20technology%20concept%20with%20AI%20artificial%20intelligence%20symbols%2C%20automated%20processes%20visualization%2C%20modern%20tech%20illustration%20with%20clean%20gradient%20background&width=400&height=300&seq=feature-automation-004&orientation=landscape" 
                  alt="Automation Feature" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8 space-y-4">
                <div className="inline-block px-4 py-1 bg-[#B8FF3C] text-gray-900 text-xs font-bold rounded-full whitespace-nowrap">AUTOMATION</div>
                <h4 className="text-2xl font-bold text-gray-900">Smart Optimization</h4>
                <p className="text-gray-600 leading-relaxed">
                  Intelligent algorithms automatically detect and remove unnecessary files to keep your device running smoothly.
                </p>
              </div>
            </div>

            {/* Feature Card 6 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 border border-gray-100">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=24%2F7%20customer%20support%20concept%20with%20headset%20and%20communication%20icons%2C%20professional%20service%20illustration%2C%20modern%20help%20desk%20theme%20with%20clean%20design&width=400&height=300&seq=feature-support-005&orientation=landscape" 
                  alt="Support Feature" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8 space-y-4">
                <div className="inline-block px-4 py-1 bg-[#B8FF3C] text-gray-900 text-xs font-bold rounded-full whitespace-nowrap">SUPPORT</div>
                <h4 className="text-2xl font-bold text-gray-900">24/7 Customer Support</h4>
                <p className="text-gray-600 leading-relaxed">
                  Our dedicated support team is always ready to help you with any questions or issues you may encounter.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-gradient-to-br from-[#0A2F2F] to-[#0f4444] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 1000 1000">
            <path d="M100,300 Q250,100 400,300 T700,300" stroke="currentColor" fill="none" strokeWidth="2" className="text-[#00E5CC]"/>
            <path d="M200,500 Q350,300 500,500 T800,500" stroke="currentColor" fill="none" strokeWidth="2" className="text-[#00E5CC]"/>
            <circle cx="200" cy="200" r="50" stroke="currentColor" fill="none" strokeWidth="2" className="text-[#00E5CC]"/>
            <circle cx="800" cy="300" r="70" stroke="currentColor" fill="none" strokeWidth="2" className="text-[#00E5CC]"/>
          </svg>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 mb-20">
            <div className="space-y-8">
              <div className="flex items-center gap-3">
                <img src="https://public.readdy.ai/ai/img_res/01637527-6d22-443a-bf67-fb4581f8a33b.png" alt="BANARICA LTDA Logo" className="h-10 w-10 object-contain" />
                <h4 className="text-3xl font-bold text-white">BANARICA LTDA<span className="text-[#00E5CC]">.</span></h4>
              </div>
              
              <div className="space-y-4">
                <h5 className="text-4xl font-bold text-white leading-tight">Stay Connected</h5>
                <h5 className="text-4xl font-bold text-white leading-tight">With Innovation</h5>
              </div>
            </div>
            
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
                <div className="space-y-4">
                  <h5 className="text-white font-bold text-lg mb-6 whitespace-nowrap">Products</h5>
                  <ul className="space-y-3">
                    <li><a href="#products" className="text-gray-300 hover:text-[#00E5CC] transition-colors text-sm cursor-pointer whitespace-nowrap">VPN</a></li>
                    <li><a href="#products" className="text-gray-300 hover:text-[#00E5CC] transition-colors text-sm cursor-pointer whitespace-nowrap">Cleaner</a></li>
                    <li><a href="#products" className="text-gray-300 hover:text-[#00E5CC] transition-colors text-sm cursor-pointer whitespace-nowrap">Updates</a></li>
                  </ul>
                </div>
                
                <div className="space-y-4">
                  <h5 className="text-white font-bold text-lg mb-6 whitespace-nowrap">Company</h5>
                  <ul className="space-y-3">
                    <li><a href="#about" className="text-gray-300 hover:text-[#00E5CC] transition-colors text-sm cursor-pointer whitespace-nowrap">About</a></li>
                    <li><a href="#about" className="text-gray-300 hover:text-[#00E5CC] transition-colors text-sm cursor-pointer whitespace-nowrap">Team</a></li>
                    <li><a href="#about" className="text-gray-300 hover:text-[#00E5CC] transition-colors text-sm cursor-pointer whitespace-nowrap">Careers</a></li>
                  </ul>
                </div>
                
                <div className="space-y-4">
                  <h5 className="text-white font-bold text-lg mb-6 whitespace-nowrap">Support</h5>
                  <ul className="space-y-3">
                    <li><a href="#contact" className="text-gray-300 hover:text-[#00E5CC] transition-colors text-sm cursor-pointer whitespace-nowrap">Help Center</a></li>
                    <li><a href="#contact" className="text-gray-300 hover:text-[#00E5CC] transition-colors text-sm cursor-pointer whitespace-nowrap">Documentation</a></li>
                    <li><a href="#contact" className="text-gray-300 hover:text-[#00E5CC] transition-colors text-sm cursor-pointer whitespace-nowrap">FAQ</a></li>
                  </ul>
                </div>
                
                <div className="space-y-4">
                  <h5 className="text-white font-bold text-lg mb-6 whitespace-nowrap">Contact</h5>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-map-pin-line text-[#00E5CC] mt-1 w-4 h-4 flex items-center justify-center"></i>
                      <span>Av. CASTELO BRANCO 541</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-building-line text-[#00E5CC] mt-1 w-4 h-4 flex items-center justify-center"></i>
                      <span>NOVA PORTEIRINHA</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-mail-line text-[#00E5CC] mt-1 w-4 h-4 flex items-center justify-center"></i>
                      <span>39525-972</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-global-line text-[#00E5CC] mt-1 w-4 h-4 flex items-center justify-center"></i>
                      <span>Brazil</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-mail-send-line text-[#00E5CC] mt-1 w-4 h-4 flex items-center justify-center"></i>
                      <a href="mailto:info@banaricaltda.com" className="hover:text-[#00E5CC] transition-colors">info@banaricaltda.com</a>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-phone-line text-[#00E5CC] mt-1 w-4 h-4 flex items-center justify-center"></i>
                      <a href="tel:+12028408194" className="hover:text-[#00E5CC] transition-colors whitespace-nowrap">+1 2028408194</a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="pt-12 border-t border-gray-700/50 flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-gray-400 text-sm whitespace-nowrap">© 2025 BANARICA LTDA. All rights reserved.</p>
            
            <div className="flex items-center gap-5">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-gray-600 rounded-lg hover:border-[#00E5CC] hover:bg-[#00E5CC]/10 transition-all cursor-pointer">
                <i className="ri-linkedin-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-gray-600 rounded-lg hover:border-[#00E5CC] hover:bg-[#00E5CC]/10 transition-all cursor-pointer">
                <i className="ri-twitter-x-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-gray-600 rounded-lg hover:border-[#00E5CC] hover:bg-[#00E5CC]/10 transition-all cursor-pointer">
                <i className="ri-facebook-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center border border-gray-600 rounded-lg hover:border-[#00E5CC] hover:bg-[#00E5CC]/10 transition-all cursor-pointer">
                <i className="ri-instagram-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
              </a>
            </div>
            
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#00E5CC] text-sm transition-colors cursor-pointer whitespace-nowrap">Powered by Readdy</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-6">
        <div className="max-w-7xl mx-auto px-8 text-center">
          <p className="text-gray-500 text-sm">Developed with precision and care for iOS users worldwide</p>
        </div>
      </footer>
    </div>
  );
}
